/**
 * The name a copy is OFFERED, and the fact that it is only an offer.
 *
 * WHAT WAS WRONG
 * --------------
 * «Kopyala» minted `${name} (kopya)` and created the template on the spot. Copy a copy and you got
 * «… (kopya) (kopya)»; copy that and the chain grew again, and nobody was ever asked what the new
 * template should be called. The copy now opens a dialog, and this is what its name box starts
 * with — editable, and never written anywhere until somebody presses «Kopyayı oluştur».
 *
 * WHAT IS SIMPLIFIED, AND WHAT IS NEVER TOUCHED
 * ---------------------------------------------
 * Only the SUGGESTION is simplified: a trailing run of «(kopya)» and «(kopya N)» is read off the
 * source's name so the offer is «X (kopya 2)» rather than «X (kopya) (kopya)». The source template
 * is not renamed, no older template's name is cleaned up, and nothing here is a rule about what a
 * name may be — a person may type «X (kopya) (kopya)» if they want it.
 */

/** «… (kopya)» and «… (kopya 2)», as the reference's own naming of a copy writes them. */
const COPY_SUFFIX = /\s*\(kopya(?:\s+(\d+))?\)$/u;

/** The name with every trailing copy suffix read off, and how many the last one counted. */
function withoutCopySuffixes(name: string): { base: string; highest: number } {
  let base = name.trim();
  let highest = 0;
  for (;;) {
    const hit = COPY_SUFFIX.exec(base);
    if (hit === null) break;
    const n = hit[1] === undefined ? 1 : Number.parseInt(hit[1], 10);
    if (Number.isFinite(n)) highest = Math.max(highest, n);
    base = base.slice(0, hit.index).trimEnd();
  }
  return { base: base === '' ? name.trim() : base, highest };
}

const copyNamed = (base: string, n: number): string => (n <= 1 ? `${base} (kopya)` : `${base} (kopya ${n})`);

/**
 * What the copy dialog's name box starts with: the first «(kopya N)» of this base that nobody is
 * using. `taken` is the library's own names, compared case-insensitively in Turkish so «KOPYA» and
 * «kopya» are not offered as two different templates.
 */
export function suggestedCopyName(name: string, taken: Iterable<string> = []): string {
  const used = new Set([...taken].map((x) => x.trim().toLocaleLowerCase('tr')));
  const { base, highest } = withoutCopySuffixes(name);
  for (let n = Math.max(1, highest + 1); n < 1000; n += 1) {
    const candidate = copyNamed(base, n);
    if (!used.has(candidate.toLocaleLowerCase('tr'))) return candidate;
  }
  return copyNamed(base, highest + 1);
}
